import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getOverlappingBaseBrokerage, getOverlappingPopScopes } from "../../Services/Brokerage";
import { errorTost } from "../../Services/Toast";

import {
  getActiveProjects,
  checkDuplicateBasePlanName,
  getWingsByProject,
} from "../../Services/Brokerage";

import DropDown from "../../Subcomponents/DropDown";

const steps = [
  { label: "Project Scope", sub: "Select projects" },
  { label: "Brokerage Type", sub: "Choose calculation" },
  { label: "Status", sub: "Set status" },
  { label: "Review", sub: "Confirm & save" },
];

const CreateBasicBrokeragePlan = ({ data, setData, onNext }) => {
  const {
    planName,
    scopeType = "project",
    scopeMode = "single",
    selectedProject,
    selectedProjects = [],
    selectedProjectWings = [],
  } = data;

  const [projects, setProjects] = useState([]);
  const [wings, setWings] = useState({}); // ✅ project-wise wings
  const [expandedProjects, setExpandedProjects] = useState([]); // ✅ multiple expand
  const [overlapMap, setOverlapMap] = useState({});

  const [nameError, setNameError] = useState("");

  /* ---------------- Duplicate Name ---------------- */
  // useEffect(() => {
  //   if (!planName?.trim()) {
  //     setNameError("");
  //     return;
  //   }

  //   const t = setTimeout(async () => {
  //     const isDuplicate = await checkDuplicateBasePlanName(planName);
  //     setNameError(isDuplicate ? "Plan name already exists" : "");
  //   }, 500);

  //   return () => clearTimeout(t);
  // }, [planName]);

  /* ---------------- Load Projects ---------------- */
  useEffect(() => {
    const load = async () => {
      const res = await getActiveProjects();

      setProjects(
        res.map((p) => ({
          id: p.projectId,
          label: p.projectName,
        }))
      );
    };

    load();
  }, []);

  /* ---------------- Project Toggle ---------------- */
  const toggleProject = (id) => {
    setData((prev) => ({
      ...prev,
      selectedProjects: prev.selectedProjects.includes(id)
        ? prev.selectedProjects.filter((p) => p !== id)
        : [...prev.selectedProjects, id],
    }));
  };

  /* ---------------- Expand Project ---------------- */
  const handleExpandProject = async (projId) => {
    try {
      // =========================
      // 🔥 HANDLE EXPAND LOGIC
      // =========================
      if (scopeType === "wing" && scopeMode === "single") {
        if (!expandedProjects.includes(projId)) {
          setData((prev) => ({
            ...prev,
            selectedProjectWings: [],
          }));
        }

        setExpandedProjects([projId]);
      } else {
        const isExpanded = expandedProjects.includes(projId);

        if (isExpanded) {
          setExpandedProjects((prev) =>
            prev.filter((id) => id !== projId)
          );
          return;
        }

        setExpandedProjects((prev) => [...prev, projId]);
      }

      // =========================
      // 🔥 FETCH WINGS (if not loaded)
      // =========================
      let projectWings = wings[projId];

      if (!projectWings) {
        const res = await getWingsByProject(projId);

        projectWings = res || [];

        setWings((prev) => ({
          ...prev,
          [projId]: projectWings,
        }));
      }

      // =========================
      // 🔥 FETCH OVERLAP (IMPORTANT FIX)
      // =========================
      if (data.startDate && data.endDate) {
        const startEpoch = new Date(data.startDate).setHours(0, 0, 0, 0);
        const endEpoch = new Date(data.endDate).setHours(23, 59, 59, 999);

        const overlap = await getOverlappingBaseBrokerage(
          projId,
          startEpoch,
          endEpoch
        );

        // ✅ store overlap per project
        setOverlapMap((prev) => ({
          ...prev,
          [projId]: overlap,
        }));

        console.log("Overlap for project:", projId, overlap);
      }
    } catch (err) {
      console.error("Expand Project Error:", err);
    }
  };

  /* ---------------- Toggle Wing ---------------- */
  const toggleWing = (projectId, wingId) => {
    setData((prev) => {
      // 🔥 SINGLE MODE → force only ONE project
      if (scopeType === "wing" && scopeMode === "single") {
        const existing = prev.selectedProjectWings[0];

        let wingIds = existing?.wingIds || [];

        if (wingIds.includes(wingId)) {
          wingIds = wingIds.filter((w) => w !== wingId);
        } else {
          wingIds = [...wingIds, wingId];
        }

        return {
          ...prev,
          selectedProjectWings: [{ projectId, wingIds }], // 🔥 only ONE project
        };
      }

      // MULTIPLE MODE (your existing logic)
      const existing = prev.selectedProjectWings?.find(
        (p) => p.projectId === projectId
      );

      let updated;

      if (existing) {
        const wingExists = existing.wingIds.includes(wingId);

        updated = prev.selectedProjectWings.map((p) =>
          p.projectId === projectId
            ? {
              ...p,
              wingIds: wingExists
                ? p.wingIds.filter((w) => w !== wingId)
                : [...p.wingIds, wingId],
            }
            : p
        );
      } else {
        updated = [
          ...prev.selectedProjectWings,
          { projectId, wingIds: [wingId] },
        ];
      }

      return { ...prev, selectedProjectWings: updated };
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {/* STEPPER */}
      <Stepper
        activeStep={0}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepIcon-root": { fontSize: "3rem" },
          "& .MuiStepIcon-root.Mui-active": { color: "#2563EB" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#2563EB" },
          "& .MuiStepLabel-label": {
            fontSize: "15px",
            fontWeight: 600,
            mt: 1,
          },
          "& .MuiStepConnector-line": {
            borderTopWidth: 3,
            borderColor: "#E5E7EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography fontWeight={600}>{step.label}</Typography>
              <Typography fontSize={13} color="text.secondary">
                {step.sub}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box display="grid" gridTemplateColumns="2.2fr 1fr" gap={4}>
        {/* LEFT */}
        <Box sx={{ p: 4, border: "1px solid #E5E7EB", borderRadius: 3 }}>
          <Typography fontSize={20} fontWeight={700} mb={3}>
            Project Scope
          </Typography>

          {/* PLAN NAME */}
          <Box mb={4}>
            <Typography fontWeight={600} mb={1}>
              Plan Name *
            </Typography>

            <TextField
              fullWidth
              value={planName}
              onChange={(e) => {
                setData({ ...data, planName: e.target.value });
                setNameError(""); // clear error while typing
              }}

              error={!!nameError}
            />
          </Box>



          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              mb: 3,
            }}
          >
            {/* Start Date */}
            <Box>
              <Typography fontWeight={500} mb={1}>
                Start Date
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                value={data.startDate || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    startDate: e.target.value,
                  })
                }
              />
            </Box>

            {/* End Date */}
            <Box>
              <Typography fontWeight={500} mb={1}>
                End Date
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                value={data.endDate || ""}
                inputProps={{ min: data.startDate }}
                onChange={(e) =>
                  setData({
                    ...data,
                    endDate: e.target.value,
                  })
                }
              />
            </Box>
          </Box>
          {/* 🔥 SCOPE + MODE IN ONE ROW */}
          <Box display="flex" gap={6} mb={3}>

            {/* LEFT → SCOPE TYPE */}
            <Box flex={1}>
              <Typography fontWeight={600} mb={2}>
                Scope Type
              </Typography>

              <RadioGroup
                value={scopeType}
                onChange={(e) =>
                  setData({
                    ...data,
                    scopeType: e.target.value,
                    scopeMode: "single",
                    selectedProject: "",
                    selectedProjects: [],
                    selectedProjectWings: [],
                  })
                }
              >
                <FormControlLabel
                  value="project"
                  control={<Radio />}
                  label="Project Based"
                />
                <FormControlLabel
                  value="wing"
                  control={<Radio />}
                  label="Wing Based"
                />
              </RadioGroup>
            </Box>

            {/* RIGHT → SELECTION TYPE */}
            <Box flex={1}>
              <Typography fontWeight={600} mb={2}>
                Selection Type
              </Typography>

              <RadioGroup
                value={scopeMode}
                onChange={(e) =>
                  setData({
                    ...data,
                    scopeMode: e.target.value,
                    selectedProject: "",
                    selectedProjects: [],
                    selectedProjectWings: [],
                  })
                }
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label="Single"
                />
                <FormControlLabel
                  value="multiple"
                  control={<Radio />}
                  label="Multiple"
                />
              </RadioGroup>
            </Box>

          </Box>


          {/* PROJECT BASED */}
          {scopeType === "project" && scopeMode === "single" && (
            <Box mt={3}>
              <DropDown
                list={[
                  { label: "Select Project", value: "" },
                  ...projects.map((p) => ({
                    label: p.label,
                    value: p.id,
                  })),
                ]}
                selectedValue={selectedProject}
                onChangeSelect={(val) =>
                  setData({ ...data, selectedProject: val })
                }
              />
            </Box>
          )}

          {scopeType === "project" && scopeMode === "multiple" && (
            <Box mt={3} display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              {projects.map((p) => (
                <Box
                  key={p.id}
                  onClick={() => toggleProject(p.id)}
                  sx={{
                    p: 2,
                    border: selectedProjects.includes(p.id)
                      ? "2px solid #4F6EF7"
                      : "1px solid #E5E7EB",
                    borderRadius: 2,
                    cursor: "pointer",
                    background: selectedProjects.includes(p.id)
                      ? "#EEF2FF"
                      : "#fff",
                  }}
                >
                  {p.label}
                </Box>
              ))}
            </Box>
          )}

          {/* WING BASED */}
          {scopeType === "wing" && (
            <Box mt={3}>
              {projects.map((project) => (
                <Box key={project.id} mb={2}>
                  <Box
                    onClick={() => handleExpandProject(project.id)}
                    sx={{
                      p: 2,
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      cursor: "pointer",
                    }}
                  >
                    <Typography fontWeight={600}>
                      {project.label}
                    </Typography>
                  </Box>

                  {expandedProjects.includes(project.id) && (
                    <Box mt={2} ml={2} display="flex" gap={2} flexWrap="wrap">
                      {(wings[project.id] || []).map((wing) => {

                        // 🔥 ADD HERE (inside map, before return)
                        const overlapWings = overlapMap[project.id]?.wings || new Set();
                        const isDisabled = overlapWings.has(wing.wingId);

                        const selected =
                          selectedProjectWings
                            ?.find((p) => p.projectId === project.id)
                            ?.wingIds.includes(wing.wingId) || false;

                        return (
                          <Box
                            key={wing.wingId}
                            onClick={() => {
                              if (isDisabled) return;
                              toggleWing(project.id, wing.wingId);
                            }}
                            sx={{
                              p: 2,
                              border: selected
                                ? "2px solid #4F6EF7"
                                : "1px solid #E5E7EB",
                              borderRadius: 2,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              background: isDisabled
                                ? "#F3F4F6"
                                : selected
                                  ? "#EEF2FF"
                                  : "#fff",
                              opacity: isDisabled ? 0.6 : 1,
                            }}
                          >
                            {wing.wingName}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* RIGHT */}
        <Box>
          <Box sx={{ p: 3, border: "1px solid #E5E7EB", borderRadius: 3 }}>
            <Typography fontWeight={700} mb={2}>
              Business Rules
            </Typography>

            {[
              "Calculated after agreement completion",
              "Paid immediately",
              "Cannot be overridden",
              "Always shown on CP dashboard",
            ].map((r) => (
              <Box key={r} display="flex" gap={1} mb={1}>
                <CheckCircleIcon sx={{ color: "#16A34A" }} />
                <Typography fontSize={13}>{r}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box display="flex" justifyContent="space-between" mt={5}>
        <Button disabled startIcon={<ArrowBackIcon />}>
          Back
        </Button>

        <Button
          variant="contained"
          onClick={async () => {
            try {
              // =========================
              // 🔥 PLAN NAME VALIDATION (NEW)
              // =========================
              if (!data.planName?.trim()) {
                setNameError("Plan name is required");
                errorTost("Plan name is required");
                return;
              }

              const isDuplicate = await checkDuplicateBasePlanName(data.planName);

              if (isDuplicate) {
                setNameError("Plan name already exists");
                errorTost("Plan name already exists");
                return;
              }

              // =========================
              // 🔥 DATE VALIDATION
              // =========================
              if (!data.startDate || !data.endDate) {
                errorTost("Please select start and end date");
                return;
              }

              if (new Date(data.endDate) < new Date(data.startDate)) {
                errorTost("End date cannot be before start date");
                return;
              }

              const startEpoch = new Date(data.startDate).setHours(0, 0, 0, 0);
              const endEpoch = new Date(data.endDate).setHours(23, 59, 59, 999);

              // =========================
              // 🔥 PROJECT BASED CHECK
              // =========================
              if (data.scopeType === "project") {
                const projectIds =
                  data.scopeMode === "single"
                    ? [data.selectedProject]
                    : data.selectedProjects;

                for (let projectId of projectIds) {
                  const overlap = await getOverlappingBaseBrokerage(
                    projectId,
                    startEpoch,
                    endEpoch
                  );

                  console.log("Overlap response:", overlap);

                  if (overlap?.projectBlocked) {
                    errorTost(
                      "Another plan already exists for this project in selected date range"
                    );
                    return;
                  }
                }
              }

              // =========================
              // 🔥 WING BASED CHECK
              // =========================
              if (data.scopeType === "wing") {
                for (let proj of data.selectedProjectWings) {
                  const overlap = await getOverlappingBaseBrokerage(
                    proj.projectId,
                    startEpoch,
                    endEpoch
                  );

                  // 🚫 project-level block
                  if (overlap?.projectBlocked) {
                    errorTost(
                      "Project already has plan in selected date range"
                    );
                    return;
                  }

                  // 🚫 wing-level block
                  const blockedWing = proj.wingIds.some((wingId) =>
                    overlap?.wings?.has(wingId)
                  );

                  if (blockedWing) {
                    errorTost(
                      "One or more selected wings already have plans in this date range"
                    );
                    return;
                  }
                }
              }

              // =========================
              // ✅ ALL GOOD → NEXT STEP
              // =========================
              onNext();

            } catch (err) {
              console.error(err);
              errorTost("Failed to validate data");
            }
          }}
          disabled={
            !planName ||
            !!nameError ||
            !data.startDate ||
            !data.endDate ||
            (scopeType === "project" &&
              scopeMode === "single" &&
              !selectedProject) ||
            (scopeType === "project" &&
              scopeMode === "multiple" &&
              selectedProjects.length === 0) ||
            (scopeType === "wing" &&
              (!selectedProjectWings ||
                selectedProjectWings.length === 0 ||
                selectedProjectWings.every(p => p.wingIds.length === 0)))
          }
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreateBasicBrokeragePlan;